import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCreateProjectPlanDraft,
  useProjectPlanDraft,
  useReviseProjectPlanDraft,
  useRetryProjectPlanDraft,
  useUpdateProjectPlanDraftPlan,
  PROJECT_PLAN_DRAFT_QUERY_KEY,
} from '@hooks/api/projectPlanDrafts';
import { TaskTemplateData } from '@models/taskTemplate.model';
import { TaskTreeEditPatch } from '@components/molecules/TaskTree';
import { removeTaskWithSubtree } from '@utils/projectPlanDraft.utils';
import { PlanDraftPurpose } from '@models/projectPlanDraft.model';

export type PlanSessionPhase = 'brief' | 'generating' | 'review';

const EDIT_DEBOUNCE_MS = 800;

/**
 * Sprint 31, story-161. Phase state machine, polling, chat, inline-edit flush — the web
 * counterpart to `redesign`'s `usePlanRevisions`. Owns exactly one draft session at a time;
 * `draftId` is controlled from outside (the URL's `?draft=` param on web) so the caller decides
 * resume vs fresh.
 */
export function usePlanDraftSession(initialDraftId?: string) {
  const [draftId, setDraftId] = useState<string | undefined>(initialDraftId);
  const queryClient = useQueryClient();

  const { data: draft, isLoading: isLoadingDraft, isError: isDraftError } = useProjectPlanDraft(draftId);

  // The caller controls the draft id through the URL: follow it when it points at another draft
  // (e.g. reopening a different in-progress plan). Never carries an id over from stale state.
  useEffect(() => {
    if (initialDraftId && initialDraftId !== draftId) setDraftId(initialDraftId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDraftId]);
  const createMutation = useCreateProjectPlanDraft();
  const reviseMutation = useReviseProjectPlanDraft();
  const retryMutation = useRetryProjectPlanDraft();
  const updatePlanMutation = useUpdateProjectPlanDraftPlan();

  const pendingEditRef = useRef<TaskTemplateData[] | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    },
    [],
  );

  const phase: PlanSessionPhase = !draftId || (isLoadingDraft && !draft) ? 'brief' : draft?.status === 'GENERATING' ? 'generating' : 'review';

  const generate = useCallback(
    async (input: {
      brief?: string;
      files?: File[];
      projectId?: string;
      purpose?: PlanDraftPurpose;
      startDate?: string;
      endDate?: string;
      projectInput?: Record<string, unknown>;
    }) => {
      const created = await createMutation.mutateAsync(input);
      setDraftId(created.draftId);
      return created;
    },
    [createMutation],
  );

  /** Sends any debounced inline edit immediately — a revision must never silently discard it. */
  const flushPendingEdit = useCallback(async () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (pendingEditRef.current && draftId) {
      const tasks = pendingEditRef.current;
      pendingEditRef.current = null;
      await updatePlanMutation.mutateAsync({ draftId, tasks });
    }
  }, [draftId, updatePlanMutation]);

  const editTask = useCallback(
    (tempId: string, patch: TaskTreeEditPatch) => {
      // Sprint 31 review fix (story-161, Suggestion #1): defensive guard mirroring
      // `sendMessage`/`retry`'s draft-id checks — busy-state locking was previously enforced only
      // by the presentation layer (disabled props passed down to TaskTree/PlanReview), so a stale
      // re-render or a future caller that forgets to check `disabled` could queue an edit during
      // GENERATING. `updatePlanMutation`'s own service call would likely 400 (AI_PLANNING_BUSY)
      // regardless, but this avoids ever attempting it and avoids the optimistic cache write below.
      if (!draft || draft.status !== 'READY') return;
      const nextPlan = draft.plan.map((task) => (task.tempId === tempId ? { ...task, ...patch } : task));
      pendingEditRef.current = nextPlan;
      // Optimistic local update so the field reflects the edit immediately, without waiting on
      // the debounced network round trip.
      queryClient.setQueryData([PROJECT_PLAN_DRAFT_QUERY_KEY, draftId], { ...draft, plan: nextPlan });

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        void flushPendingEdit();
      }, EDIT_DEBOUNCE_MS);
    },
    [draft, draftId, queryClient, flushPendingEdit],
  );

  /** Removes a task and its subtree via the same debounced patch path as an inline edit. */
  const deleteTask = useCallback(
    (tempId: string) => {
      if (!draft || draft.status !== 'READY') return;
      const nextPlan = removeTaskWithSubtree(draft.plan, tempId);
      pendingEditRef.current = nextPlan;
      queryClient.setQueryData([PROJECT_PLAN_DRAFT_QUERY_KEY, draftId], { ...draft, plan: nextPlan });
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      void flushPendingEdit();
    },
    [draft, draftId, queryClient, flushPendingEdit],
  );

  const sendMessage = useCallback(
    async (message: string) => {
      if (!draftId) return;
      // Edit-then-revise ordering: a chat send always flushes any pending debounced edit first.
      await flushPendingEdit();
      await reviseMutation.mutateAsync({ draftId, message });
    },
    [draftId, flushPendingEdit, reviseMutation],
  );

  const retry = useCallback(async () => {
    if (!draftId) return;
    await retryMutation.mutateAsync(draftId);
  }, [draftId, retryMutation]);

  const reset = useCallback(() => {
    setDraftId(undefined);
  }, []);

  return {
    phase,
    draft,
    draftId,
    setDraftId,
    generate,
    sendMessage,
    editTask,
    deleteTask,
    retry,
    reset,
    isGenerating: draft?.status === 'GENERATING',
    isCreating: createMutation.isPending,
    /** The id in the URL points at a draft that no longer exists (discarded, imported elsewhere). */
    isDraftMissing: !!draftId && isDraftError && !draft,
  };
}

export default usePlanDraftSession;
